import { Button } from "@microbeta/ui";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
} from "react-icons/md";
import styles from "./pagination.module.scss";

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  showFirstLast?: boolean;
  maxVisiblePages?: number;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  showFirstLast = true,
  maxVisiblePages = 5,
  className,
}) => {
  const { t } = useTranslation();

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Calculate visible page numbers
  const visiblePages = useMemo(() => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    const end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages, maxVisiblePages]);

  // Don't render pagination if there's only one page or no items
  if (totalPages <= 1) {
    return null;
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={`${styles.pagination} ${className || ""}`}>
      <div className={styles.paginationInfo}>
        <span className={styles.paginationText}>
          {t("pagination.showingItems", {
            start: startItem,
            end: endItem,
            total: totalItems,
          })}
        </span>
      </div>

      <div className={styles.paginationControls}>
        {/* First page button */}
        {showFirstLast && (
          <Button
            variant="ghost"
            size="small"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            aria-label={t("pagination.firstPage")}
            className={styles.paginationButton}
          >
            <MdFirstPage />
          </Button>
        )}

        {/* Previous page button */}
        <Button
          variant="ghost"
          size="small"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label={t("pagination.previousPage")}
          className={styles.paginationButton}
        >
          <MdChevronLeft />
          <span className={styles.paginationButtonText}>
            {t("pagination.previous")}
          </span>
        </Button>

        {/* Page number buttons */}
        <div className={styles.pageNumbers}>
          {visiblePages[0] > 1 && (
            <>
              <Button
                variant="ghost"
                size="small"
                onClick={() => handlePageChange(1)}
                className={styles.pageNumberButton}
              >
                1
              </Button>
              {visiblePages[0] > 2 && (
                <span className={styles.ellipsis}>...</span>
              )}
            </>
          )}

          {visiblePages.map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? "primary" : "ghost"}
              size="small"
              onClick={() => handlePageChange(page)}
              className={styles.pageNumberButton}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </Button>
          ))}

          {visiblePages[visiblePages.length - 1] < totalPages && (
            <>
              {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                <span className={styles.ellipsis}>...</span>
              )}
              <Button
                variant="ghost"
                size="small"
                onClick={() => handlePageChange(totalPages)}
                className={styles.pageNumberButton}
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>

        {/* Next page button */}
        <Button
          variant="ghost"
          size="small"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label={t("pagination.nextPage")}
          className={styles.paginationButton}
        >
          <span className={styles.paginationButtonText}>
            {t("pagination.next")}
          </span>
          <MdChevronRight />
        </Button>

        {/* Last page button */}
        {showFirstLast && (
          <Button
            variant="ghost"
            size="small"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            aria-label={t("pagination.lastPage")}
            className={styles.paginationButton}
          >
            <MdLastPage />
          </Button>
        )}
      </div>
    </div>
  );
};
